import React, { FC, ReactNode } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { CoreCommonMessages } from '@nhl/nhl-breakaway-ui';

import { LandingGame, TeamSeasonStat } from '../../../../types';
import { formatAsPercentage } from '../../../../utils';
import ProgressBar from '../../../progressBar/ProgressBar';

import { RankValue, SideBySideStatContainer, StatContainer, StatName, StatValue } from './styles';

type Props = {
    accessorField: string;
    rankField: string;
    awayLeader: TeamSeasonStat;
    homeLeader: TeamSeasonStat;
    game: LandingGame;
    getDisplayValue?: (leader: TeamSeasonStat) => string;
    getProgressBarValue?: (val1: number, val2: number) => number;
    title: ReactNode;
    showPercentageSymbol?: boolean;
};

const SideBySideStat: FC<Props> = ({
    game,
    awayLeader,
    homeLeader,
    accessorField,
    rankField,
    title,
    getDisplayValue,
    getProgressBarValue,
    showPercentageSymbol = false,
}) => {
    const theme = useTheme();

    const awayRank = awayLeader[rankField];
    const awayValue = awayLeader[accessorField];
    let awayText = getDisplayValue ? getDisplayValue(awayLeader) : awayValue;

    const homeRank = homeLeader[rankField];
    const homeValue = homeLeader[accessorField];
    let homeText = getDisplayValue ? getDisplayValue(homeLeader) : homeValue;

    if (showPercentageSymbol && getDisplayValue) {
        homeText = formatAsPercentage(parseFloat(homeValue) * 100);
        awayText = formatAsPercentage(parseFloat(awayValue) * 100);
    }

    return (
        <SideBySideStatContainer>
            <StatContainer>
                <StatValue>{awayText}</StatValue>
                <StatName>{title}</StatName>
                <StatValue $isHome>{homeText}</StatValue>
            </StatContainer>
            <ProgressBar
                awayTeamColor={theme.Team[game.awayTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                awayTeamValue={getProgressBarValue ? getProgressBarValue(awayValue, homeValue) : awayValue}
                homeTeamColor={theme.Team[game.homeTeam.abbrev]?.gamecenter || theme.Surface.Opposite.tertiary}
                homeTeamValue={getProgressBarValue ? getProgressBarValue(homeValue, awayValue) : homeValue}
            />
            <StatContainer>
                <RankValue>
                    <FormattedMessage {...CoreCommonMessages.misc.ordinal} values={{ value: awayRank }} />
                </RankValue>
                <RankValue $isHome>
                    <FormattedMessage {...CoreCommonMessages.misc.ordinal} values={{ value: homeRank }} />
                </RankValue>
            </StatContainer>
        </SideBySideStatContainer>
    );
};

export default SideBySideStat;
